<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="mb-8 flex flex-col items-center">
        <Title align="center">Curve Results</Title>
        <div class="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm">
          <span class="text-gray-500">Subject</span>
          <span class="px-2 py-0.5 rounded-full bg-gray-100 text-gray-900 font-medium">{{ subjectInfo.subid || 'N/A' }}</span>
          <span class="text-gray-300">•</span>
          <span class="text-gray-500">Dataset</span>
          <span class="px-2 py-0.5 rounded-full bg-gray-100 text-gray-900 font-medium">{{ subjectInfo.dataset_identifier || 'N/A' }}</span>
          <span class="text-gray-300">•</span>
          <span class="text-gray-500">Curves</span>
          <span class="px-2 py-0.5 rounded-full bg-gray-100 text-gray-900 font-medium">{{ totalCurves || 0 }}</span>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span class="ml-3 text-gray-600">Loading curve results...</span>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-md p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Error loading curve results</h3>
            <div class="mt-2 text-sm text-red-700">{{ error }}</div>
          </div>
        </div>
      </div>

      <!-- No Data State -->
      <div v-else-if="!curveFeatures || curveFeatures.length === 0" class="text-center py-12">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <h3 class="mt-2 text-sm font-medium text-gray-900">No curve data available</h3>
        <p class="mt-1 text-sm text-gray-500">No curves were detected in the processed data.</p>
      </div>

      <!-- Curve Results Content -->
      <div v-else class="space-y-6">
        <!-- Features Accordion Section -->
        <div class="bg-white border border-gray-200 rounded-md">
          <!-- Features Header with Toggle -->
          <button 
            @click="toggleFeaturesExpansion" 
            class="w-full flex items-center justify-between px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors border-b border-gray-200"
          >
            <div class="flex items-center">
              <h3 class="text-lg font-semibold text-gray-900">Features</h3>
              <span class="ml-2 text-sm text-gray-500">({{ totalCurves }} curves)</span>
            </div>
            <svg 
              class="w-5 h-5 text-gray-500 transition-transform duration-200" 
              :class="isFeaturesExpanded ? 'rotate-180' : ''"
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          <!-- Collapsible Features Content -->
          <div v-if="isFeaturesExpanded" class="p-6">
            <!-- Summary Statistics with Download Button -->
            <div class="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 text-sm text-gray-700 mb-6">
              <div class="flex flex-wrap items-center gap-x-6 gap-y-2">
                <div>
                  <span class="text-gray-500 mr-1">Total Curves:</span>
                  <span class="font-semibold">{{ totalCurves }}</span>
                </div>
                <div>
                  <span class="text-gray-500 mr-1">High-Quality:</span>
                  <span class="font-semibold text-green-700">{{ validCount }}</span>
                </div>
                <div>
                  <span class="text-gray-500 mr-1">Low-Quality:</span>
                  <span class="font-semibold text-red-700">{{ invalidCount }}</span>
                </div>
              </div>
              <!-- Download Button -->
              <div>
                <button
                  @click="downloadFeatureStatistics"
                  :disabled="!canExportFeatures"
                  class="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  :title="canExportFeatures ? 'Download feature statistics as Excel' : 'No data available for export'"
                >
                  Download Statistics
                </button>
              </div>
            </div>

            <!-- Curve Metrics Table -->
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200 text-sm">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-4 py-2 text-left font-medium text-gray-700">Metric</th>
                    <th class="px-4 py-2 text-center font-medium text-gray-700">High-Quality</th>
                    <th class="px-4 py-2 text-center font-medium text-gray-700">Low-Quality</th>
                    <th class="px-4 py-2 text-center font-medium text-gray-700">Total</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                  <tr 
                    v-for="row in displayCurveRows" 
                    :key="row.type + '-' + row.key" 
                    :class="row.type === 'main' ? 'hover:bg-gray-50 cursor-pointer' : 'bg-gray-50'" 
                    @click="row.type === 'main' ? toggleCurveMetric(row.metric) : null"
                  >
                    <template v-if="row.type === 'main'">
                      <td class="px-4 py-2 text-gray-700">{{ row.metric.label }}</td>
                      <td class="px-4 py-2 text-center">{{ curveStats.valid[row.metric.key]?.display || '–' }}</td>
                      <td class="px-4 py-2 text-center">{{ curveStats.invalid[row.metric.key]?.display || '–' }}</td>
                      <td class="px-4 py-2 text-center">{{ curveStats.total[row.metric.key]?.display || '–' }}</td>
                    </template>
                    <template v-else>
                      <td :colspan="4" class="px-4 py-3">
                        <div class="p-3 border border-gray-200 rounded-lg bg-white">
                          <CurveFeatureComparisonChart
                            :valid-values="valuesForMetric('valid', row.metric.key)"
                            :invalid-values="valuesForMetric('invalid', row.metric.key)"
                            :metric-label="row.metric.label"
                            :valid-curve-indices="validCurveIndices"
                            :invalid-curve-indices="invalidCurveIndices"
                          />
                        </div>
                      </td>
                    </template>
                  </tr>
                </tbody>
              </table>
              <!-- Table Note -->
              <div class="mt-3 text-xs text-gray-600 italic">
                <p>Values shown as mean [min, max]. Click on any row to view distribution comparison chart.</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Flags Accordion Section -->
        <div class="bg-white border border-gray-200 rounded-md">
        <!-- Flags Header with Toggle -->
        <button 
          @click="toggleFlagsExpansion" 
          class="w-full flex items-center justify-between px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors border-b border-gray-200"
        >
          <div class="flex items-center">
            <h3 class="text-lg font-semibold text-gray-900">Flags</h3>
            <span class="ml-2 text-sm text-gray-500">({{ flaggedCount }} flagged curves)</span>
          </div>
          <svg 
            class="w-5 h-5 text-gray-500 transition-transform duration-200" 
            :class="isFlagsExpanded ? 'rotate-180' : ''"
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        <!-- Collapsible Flags Content -->
        <div v-if="isFlagsExpanded" class="p-6">
          <!-- Manual Overrides summary list -->
          <div v-if="overriddenCurveEntries && overriddenCurveEntries.length" class="mb-4">
            <div class="text-sm text-gray-700 mb-1">
              <span class="font-medium">Manual Overrides: </span>
              <span>{{ overriddenCurveEntries.length }} {{ overriddenCurveEntries.length === 1 ? 'curve' : 'curves' }}</span>
            </div>
            <div class="text-sm text-blue-700">
              <span class="mr-1">Curve IDs:</span>
              <span v-for="(entry, i) in overriddenCurveEntries" :key="'override-'+entry.index">
                <button 
                  type="button"
                  class="underline hover:no-underline text-blue-700 focus:outline-none"
                  :title="`Open Data Explorer for curve ${entry.id}`"
                  @click="openDataExplorerSameTab(entry.index)"
                  @keydown.enter.prevent="openDataExplorerSameTab(entry.index)"
                >
                  {{ entry.displayId }}
                </button><span v-if="i < overriddenCurveEntries.length - 1">, </span>
              </span>
            </div>
          </div>
          <!-- Summary Statistics with Download Button -->
          <div class="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 text-sm text-gray-700 mb-6">
            <div class="flex flex-wrap items-center gap-x-6 gap-y-2">
              <div>
                <span class="text-gray-500 mr-1">Total Curves:</span>
                <span class="font-semibold">{{ totalCurves }}</span>
              </div>
              <div>
                <span class="text-gray-500 mr-1">Flagged:</span>
                <span class="font-semibold text-red-700">{{ flaggedCount }}</span>
              </div>
              <div>
                <span class="text-gray-500 mr-1">Unflagged:</span>
                <span class="font-semibold text-green-700">{{ unflaggedCount }}</span>
              </div>
            </div>
            <!-- Download Button -->
            <div>
              <button
                @click="downloadFlagStatistics"
                :disabled="!canExportFlags"
                class="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
                :title="canExportFlags ? 'Download flag statistics as Excel' : 'No data available for export'"
              >
                Download Flag Summary
              </button>
            </div>
          </div>

          <!-- Flags Table -->
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 text-sm">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-4 py-2 text-left font-medium text-gray-700">Region</th>
                    <th class="px-4 py-2 text-left font-medium text-gray-700">Flag Name</th>
                    <th class="px-4 py-2 text-left font-medium text-gray-700">Threshold Metric</th>
                    <th class="px-4 py-2 text-center font-medium text-gray-700">Flag Count</th>
                  </tr>
                </thead>
              <tbody class="divide-y divide-gray-100 bg-white">
                <tr 
                  v-for="row in displayFlagRows" 
                  :key="row.key" 
                  :class="row.type === 'main' ? 'hover:bg-gray-50 cursor-pointer' : ''" 
                  @click="row.type === 'main' ? toggleFlagMetric(row.metric) : null"
                >
                  <template v-if="row.type === 'main'">
                    <td class="px-4 py-2 text-gray-700 font-medium">
                      <span v-if="row.isFirstInCategory">{{ row.category }}</span>
                    </td>
                    <td class="px-4 py-2 text-gray-700 font-medium">
                      <span v-if="row.isFirstInFlag">{{ row.flagName }}</span>
                    </td>
                    <td class="px-4 py-2 text-gray-700">
                      {{ row.metric.label }}
                    </td>
                    <td class="px-4 py-2 text-center">
                      <span 
                        v-if="row.showFlagCount"
                        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                        :class="getFlagStatusClass(row.actualFlagName)"
                      >
                        {{ getFlagStatusText(row.actualFlagName) }}
                      </span>
                    </td>
                  </template>
                  <template v-else>
                    <td :colspan="4" class="px-4 py-3">
                      <div class="p-3 border border-gray-200 rounded-lg bg-white">
                        <FlagThresholdChart
                          :all-values="valuesForFlagMetric('all', row.metric.key)"
                          :value-indices="valueIndicesForFlagMetric(row.metric.key)"
                          :threshold="getFlagThreshold(row.metric.key)"
                          :metric-label="row.metric.label"
                          :flag-type="getFlagType(row.metric.key)"
                          :valid-curves="validCurveIndices"
                          :invalid-curves="invalidCurveIndices"
                        />
                      </div>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>
          
          <!-- Table Note -->
          <div class="mt-6 text-xs text-gray-600 italic">
            <p>Click on any threshold metric row to view a distribution comparison chart. Multi-threshold flags (shown with multiple threshold metric rows) can be triggered by any of their thresholds being exceeded. Flag count is shown once per flag name.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import CurveFeatureComparisonChart from '../../components/CurveFeatureComparisonChart.vue'
import FlagThresholdChart from '../../components/FlagThresholdChart.vue'
import Title from '../../components/Title.vue'
import { downloadCurveFeaturesStatistics, validateCurveFeaturesData } from '../../utils/curveFeaturesExportUtils.js'
import { downloadFlagStatistics, validateFlagStatisticsData } from '../../utils/flagStatisticsExportUtils.js'
import { demoPath } from '../../demo/paths.js'

export default {
  name: 'DemoCurveResults',
  inject: ['globalState', 'setBackButton', 'hideBackButton'],
  components: {
    CurveFeatureComparisonChart,
    FlagThresholdChart,
    Title
  },
  setup() {
    return { toLab: demoPath }
  },
  data() {
    return {
      loading: true,
      error: null,
      curveFeatures: [],
      subjectInfo: {},
      expandedCurveMetric: null,
      expandedFlagMetric: null,
      // Accordion state
      isFeaturesExpanded: false,
      isFlagsExpanded: false,
      // Flag selections from backend (for threshold display)
      flagSelections: null
    }
  },
  computed: {
    totalCurves() {
      return Array.isArray(this.curveFeatures) ? this.curveFeatures.length : 0
    },
    validCurves() {
      if (!Array.isArray(this.curveFeatures)) return []
      // Use REGION_VALID (combined curve + periphery validity) as the main quality indicator
      // Fallback to CURVE_VALID for backward compatibility
      return this.curveFeatures.filter(c => {
        const regionValid = c?.REGION_VALID
        if (regionValid !== undefined && regionValid !== null) {
          return Number(regionValid) === 1
        }
        // Fallback to CURVE_VALID if REGION_VALID not available
        return Number(c?.CURVE_VALID) === 1
      })
    },
    invalidCurves() {
      if (!Array.isArray(this.curveFeatures)) return []
      // Use REGION_VALID (combined curve + periphery validity) as the main quality indicator
      // Fallback to CURVE_VALID for backward compatibility
      return this.curveFeatures.filter(c => {
        const regionValid = c?.REGION_VALID
        if (regionValid !== undefined && regionValid !== null) {
          return Number(regionValid) !== 1
        }
        // Fallback to CURVE_VALID if REGION_VALID not available
        return Number(c?.CURVE_VALID) !== 1
      })
    },
    validCount() {
      return this.validCurves.length
    },
    invalidCount() {
      return this.invalidCurves.length
    },
    validCurveIndices() {
      if (!Array.isArray(this.curveFeatures)) return []
      return this.curveFeatures
        .map((curve, index) => ({ curve, index }))
        .filter(({ curve }) => {
          const regionValid = curve?.REGION_VALID
          if (regionValid !== undefined && regionValid !== null) {
            return Number(regionValid) === 1
          }
          // Fallback to CURVE_VALID if REGION_VALID not available
          return Number(curve?.CURVE_VALID) === 1
        })
        .map(({ index }) => index)
    },
    invalidCurveIndices() {
      if (!Array.isArray(this.curveFeatures)) return []
      return this.curveFeatures
        .map((curve, index) => ({ curve, index }))
        .filter(({ curve }) => {
          const regionValid = curve?.REGION_VALID
          if (regionValid !== undefined && regionValid !== null) {
            return Number(regionValid) !== 1
          }
          // Fallback to CURVE_VALID if REGION_VALID not available
          return Number(curve?.CURVE_VALID) !== 1
        })
        .map(({ index }) => index)
    },
    curveMetrics() {
      return [
        { key: 'peak_CURVE', label: 'Peak' },
        { key: 'auc_total_CURVE', label: 'AUC Total' },
        { key: 'duration_CURVE', label: 'Curve Duration (h)' },
        { key: 'rise_duration_CURVE', label: 'Rise Duration (h)' },
        { key: 'rise_rate_CURVE', label: 'Rise Rate' },
        { key: 'fall_duration_CURVE', label: 'Fall Duration (h)' },
        { key: 'fall_rate_CURVE', label: 'Fall Rate' }
      ]
    },
    curveStats() {
      const fmt = (v) => (v == null || isNaN(v) ? '–' : Number(v).toFixed(1))
      const statsOf = (arr, key) => {
        if (!arr.length) return null
        const vals = arr.map(r => Number(r?.[key])).filter(v => !isNaN(v))
        if (!vals.length) return null
        const count = vals.length
        const mean = vals.reduce((a, b) => a + b, 0) / count
        const min = Math.min(...vals)
        const max = Math.max(...vals)
        // Calculate standard deviation
        const variance = vals.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / count
        const sd = Math.sqrt(variance)
        // Return both string format for display and object format for export
        return {
          display: `${fmt(mean)} [${fmt(min)}, ${fmt(max)}]`,
          mean: mean,
          sd: sd,
          min: min,
          max: max,
          count: count
        }
      }
      const build = (arr) => {
        const obj = {}
        this.curveMetrics.forEach(m => {
          obj[m.key] = statsOf(arr, m.key)
        })
        return obj
      }
      return {
        total: build(this.curveFeatures),
        valid: build(this.validCurves),
        invalid: build(this.invalidCurves)
      }
    },
    canExportFeatures() {
      return this.curveFeatures && this.curveFeatures.length > 0 && this.subjectInfo.subid
    },
    displayCurveRows() {
      const rows = []
      this.curveMetrics.forEach(m => {
        rows.push({ type: 'main', key: m.key, metric: m })
        if (this.expandedCurveMetric && this.expandedCurveMetric.key === m.key) {
          rows.push({ type: 'expand', key: m.key, metric: m })
        }
      })
      return rows
    },
    flaggedCount() {
      if (!Array.isArray(this.curveFeatures)) return 0
      // Treat manual overrides as flags even though they are not shown in the table
      return this.curveFeatures.filter(c => {
        const thresholdFlagged = this.isCurveFlagged(c)
        const manuallyOverridden = Number(c?.override_flags) === 1
        return thresholdFlagged || manuallyOverridden
      }).length
    },
    unflaggedCount() {
      return this.totalCurves - this.flaggedCount
    },
    flagCategories() {
      return [
        {
          category: 'Curve',
          description: 'Flags related to overall curve data quality',
          flags: [
            { 
              key: 'unimputed_low_quality_percent_CURVE', 
              label: 'Unimputed Low Quality %',
              flagName: 'FLAG_unimputed_low_quality_curve',
              hasMultipleThresholds: true,
              relatedMetrics: ['unimputed_low_quality_duration_CURVE']
            },
            { 
              key: 'unimputed_low_quality_duration_CURVE', 
              label: 'Unimputed Low Quality Duration',
              flagName: 'FLAG_unimputed_low_quality_curve',
              isSecondaryMetric: true
            },
            { 
              key: 'imputed_percent_CURVE', 
              label: 'Imputed %',
              flagName: 'FLAG_imputed_limit_curve',
              hasMultipleThresholds: true,
              relatedMetrics: ['imputed_duration_CURVE']
            },
            { 
              key: 'imputed_duration_CURVE', 
              label: 'Imputed Duration',
              flagName: 'FLAG_imputed_limit_curve',
              isSecondaryMetric: true
            },
            { 
              key: 'unimputed_jump_percent_CURVE', 
              label: 'Unimputed Jump %',
              flagName: 'FLAG_unimputed_jump_curve'
            },
            { 
              key: 'below_threshold_percent_CURVE', 
              label: 'Below Threshold %',
              flagName: 'FLAG_below_threshold_curve'
            }
          ]
        },
        {
          category: 'Curve Rise',
          description: 'Flags related to curve rise phase completeness and quality',
          flags: [
            { 
              key: 'rise_duration_CURVE', 
              label: 'Rise Complete (minutes)',
              flagName: 'FLAG_incomplete_curve_start_curve',
              isDurationInMinutes: true
            },
            { 
              key: 'total_duration_PERIPHERY_BEFORE', 
              label: 'Periphery Before Duration (hours)',
              flagName: 'FLAG_incomplete_periphery_before_curve',
              isDurationInHours: true
            },
            { 
              key: 'rise_imputed_percent_CURVE', 
              label: 'Rise Imputed %',
              flagName: 'FLAG_imputed_rise_curve',
              hasMultipleThresholds: true,
              relatedMetrics: ['ascending_imputed_percent_CURVE']
            },
            { 
              key: 'ascending_imputed_percent_CURVE', 
              label: 'Ascending Imputed %',
              flagName: 'FLAG_imputed_rise_curve',
              isSecondaryMetric: true
            }
          ]
        },
        {
          category: 'Curve Fall',
          description: 'Flags related to curve fall phase completeness and quality',
          flags: [
            { 
              key: 'fall_duration_CURVE', 
              label: 'Fall Complete (minutes)',
              flagName: 'FLAG_incomplete_curve_end_curve',
              isDurationInMinutes: true
            },
            { 
              key: 'total_duration_PERIPHERY_AFTER', 
              label: 'Periphery After Duration (hours)',
              flagName: 'FLAG_incomplete_periphery_after_curve',
              isDurationInHours: true
            },
            { 
              key: 'fall_imputed_percent_CURVE', 
              label: 'Fall Imputed %',
              flagName: 'FLAG_imputed_fall_curve',
              hasMultipleThresholds: true,
              relatedMetrics: ['descending_imputed_percent_CURVE']
            },
            { 
              key: 'descending_imputed_percent_CURVE', 
              label: 'Descending Imputed %',
              flagName: 'FLAG_imputed_fall_curve',
              isSecondaryMetric: true
            }
          ]
        },
        {
          category: 'Periphery (Before)',
          description: 'Flags related to data quality before the curve',
          flags: [
            { 
              key: 'total_gaps_and_non_wear_percent_PERIPHERY_BEFORE', 
              label: 'Gaps/Non-wear %',
              flagName: 'FLAG_gaps_and_non_wear_periphery_before'
            },
            { 
              key: 'total_extreme_negative_percent_PERIPHERY_BEFORE', 
              label: 'Extreme Negative %',
              flagName: 'FLAG_extreme_negative_periphery_before'
            },
            { 
              key: 'unimputed_low_quality_percent_PERIPHERY_BEFORE', 
              label: 'Low Quality %',
              flagName: 'FLAG_unimputed_low_quality_periphery_before'
            }
          ]
        },
        {
          category: 'Periphery (After)',
          description: 'Flags related to data quality after the curve',
          flags: [
            { 
              key: 'total_gaps_and_non_wear_percent_PERIPHERY_AFTER', 
              label: 'Gaps/Non-wear %',
              flagName: 'FLAG_gaps_and_non_wear_periphery_after'
            },
            { 
              key: 'total_extreme_negative_percent_PERIPHERY_AFTER', 
              label: 'Extreme Negative %',
              flagName: 'FLAG_extreme_negative_periphery_after'
            },
            { 
              key: 'unimputed_low_quality_percent_PERIPHERY_AFTER', 
              label: 'Low Quality %',
              flagName: 'FLAG_unimputed_low_quality_periphery_after'
            }
          ]
        }
      ]
    },
    flagMetrics() {
      // Flatten all flags from categories for backward compatibility
      return this.flagCategories.flatMap(category => 
        category.flags.filter(flag => !flag.isSecondaryMetric)
      )
    },
    // Curves that have been manually overridden
    overriddenCurveEntries() {
      if (!Array.isArray(this.curveFeatures)) return []
      return this.curveFeatures
        .map((c, index) => {
          const id = (c?.curve_id ?? index)
          return { id, displayId: Number(id) + 1, index, override: Number(c?.override_flags) === 1 }
        })
        .filter(e => e.override)
    },
    flagStats() {
      const fmt = (v) => (v == null || isNaN(v) ? '–' : Number(v).toFixed(2))
      const statsOf = (arr, key) => {
        if (!arr.length) return null
        const vals = arr.map(r => Number(r?.[key])).filter(v => !isNaN(v))
        if (!vals.length) return null
        const count = vals.length
        const mean = vals.reduce((a, b) => a + b, 0) / count
        const min = Math.min(...vals)
        const max = Math.max(...vals)
        // Calculate standard deviation
        const variance = vals.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / count
        const sd = Math.sqrt(variance)
        // Return both string format for display and object format for export
        return {
          display: fmt(mean),
          mean: mean,
          sd: sd,
          min: min,
          max: max,
          count: count
        }
      }
      const build = (arr) => {
        const obj = {}
        this.flagMetrics.forEach(m => {
          obj[m.key] = statsOf(arr, m.key)
        })
        return obj
      }
      
      // Calculate flagged counts per flag
      const flaggedByFlag = {}
      this.flagMetrics.forEach(m => {
        const flagged = this.curveFeatures.filter(curve => {
          const flagValue = curve[m.flagName]
          return flagValue != null && Number(flagValue) === 1
        }).length
        flaggedByFlag[m.key] = flagged
      })
      
      return {
        total: build(this.curveFeatures),
        valid: build(this.validCurves),
        invalid: build(this.invalidCurves),
        flaggedByFlag: flaggedByFlag
      }
    },
    canExportFlags() {
      return this.curveFeatures && this.curveFeatures.length > 0 && this.subjectInfo.subid
    },
    displayFlagRows() {
      const rows = []
      
      // Group flags by flagName within each category
      this.flagCategories.forEach(category => {
        // Group flags by flagName
        const flagsByFlagName = {}
        category.flags.forEach(flag => {
          const flagName = flag.flagName
          if (!flagsByFlagName[flagName]) {
            flagsByFlagName[flagName] = {
              flagName: flagName,
              formattedFlagName: this.formatFlagName(flagName),
              metrics: [],
              category: category.category
            }
          }
          flagsByFlagName[flagName].metrics.push(flag)
        })
        
        // Convert grouped flags to rows
        const flagGroups = Object.values(flagsByFlagName)
        flagGroups.forEach((flagGroup, groupIndex) => {
          // Sort metrics: primary metrics first, then secondary metrics
          const sortedMetrics = flagGroup.metrics.sort((a, b) => {
            if (a.isSecondaryMetric && !b.isSecondaryMetric) return 1
            if (!a.isSecondaryMetric && b.isSecondaryMetric) return -1
            return 0
          })
          
          sortedMetrics.forEach((metric, metricIndex) => {
            const isFirstInCategory = groupIndex === 0 && metricIndex === 0
            const isFirstInFlag = metricIndex === 0
            const showFlagCount = metricIndex === 0 // Show flag count only on first metric row
            
            rows.push({
              type: 'main',
              key: `${category.category}-${flagGroup.flagName}-${metric.key}`,
              metric: metric,
              flagName: flagGroup.formattedFlagName,
              actualFlagName: flagGroup.flagName, // Store actual FLAG_* name for lookup
              category: category.category,
              isFirstInCategory: isFirstInCategory,
              isFirstInFlag: isFirstInFlag,
              showFlagCount: showFlagCount
            })
            
            // Add expanded chart row if this metric is expanded
            if (this.expandedFlagMetric && this.expandedFlagMetric.key === metric.key) {
              rows.push({
                type: 'expand',
                key: `${category.category}-${flagGroup.flagName}-${metric.key}-chart`,
                metric: metric
              })
            }
          })
        })
      })
      
      return rows
    }
  },
  methods: {
    // Format flag name from flagName (e.g., "FLAG_unimputed_low_quality_curve" -> "Unimputed Low Quality")
    // Removes region suffixes (_curve, _periphery_before, _periphery_after) since region is shown in Region column
    formatFlagName(flagName) {
      try {
        // Remove leading FLAG_
        let name = flagName.replace(/^FLAG_/, '')
        // Remove region suffixes (order matters - check longer patterns first)
        name = name.replace(/_periphery_before$/, '')
        name = name.replace(/_periphery_after$/, '')
        name = name.replace(/_curve$/, '')
        // Replace underscores with spaces and title-case words
        name = name.replace(/_/g, ' ')
        name = name.replace(/\b\w/g, c => c.toUpperCase())
        return name
      } catch (_) {
        return flagName
      }
    },
    valueIndicesForFlagMetric(key) {
      if (!Array.isArray(this.curveFeatures)) return []
      const indices = []
      this.curveFeatures.forEach((r, idx) => {
        const v = r?.[key]
        if (v !== undefined && v !== null) {
          indices.push(idx)
        }
      })
      return indices
    },
    toggleFeaturesExpansion() {
      this.isFeaturesExpanded = !this.isFeaturesExpanded
    },
    valuesForMetric(type, key) {
      const src = type === 'valid' ? this.validCurves : this.invalidCurves
      return src.map(r => r?.[key]).filter(v => v !== undefined && v !== null)
    },
    toggleCurveMetric(metric) {
      if (this.expandedCurveMetric && this.expandedCurveMetric.key === metric.key) {
        this.expandedCurveMetric = null
      } else {
        this.expandedCurveMetric = metric
      }
    },
    toggleFlagsExpansion() {
      this.isFlagsExpanded = !this.isFlagsExpanded
    },
    toggleFlagMetric(metric) {
      // Do not expand rows that are marked as "noChart" (e.g., manual overrides)
      if (metric && metric.noChart) {
        return
      }
      if (this.expandedFlagMetric && this.expandedFlagMetric.key === metric.key) {
        this.expandedFlagMetric = null
      } else {
        this.expandedFlagMetric = metric
      }
    },
    isCurveFlagged(curve) {
      // Check if any flag is set to 1 in the curve data
      return this.flagCategories.some(category => 
        category.flags
          // Exclude manual override rows from the "flagged curves" summary
          .filter(flag => !flag.isManual)
          .some(flag => {
            const flagValue = curve[flag.flagName]
            return flagValue != null && Number(flagValue) === 1
          })
      )
    },
    valuesForFlagMetric(type, key) {
      if (type === 'all') {
        return this.curveFeatures.map(r => r?.[key]).filter(v => v !== undefined && v !== null)
      }
      const src = type === 'valid' ? this.validCurves : this.invalidCurves
      return src.map(r => r?.[key]).filter(v => v !== undefined && v !== null)
    },
    // Display helper: mean percentages for threshold-based rows
    getFlagCellValue(metric, bucket) {
      const table = this.flagStats && this.flagStats[bucket]
      if (table && Object.prototype.hasOwnProperty.call(table, metric.key)) {
        const statsObj = table[metric.key]
        // Return display value if it's an object, otherwise return the value directly
        return statsObj?.display !== undefined ? statsObj.display : statsObj
      }
      return '–'
    },
    getFlagThreshold(key) {
      // Map frontend metric keys to backend flag_selections structure
      const flagMapping = {
        'unimputed_low_quality_percent_CURVE': { flag: 'flag_unimputed_low_quality_curve', param: 'percent_cutoff' },
        'unimputed_low_quality_duration_CURVE': { flag: 'flag_unimputed_low_quality_curve', param: 'duration_cutoff' },
        'imputed_percent_CURVE': { flag: 'flag_imputed_limit_curve', param: 'percent_cutoff' },
        'imputed_duration_CURVE': { flag: 'flag_imputed_limit_curve', param: 'duration_cutoff' },
        'unimputed_jump_percent_CURVE': { flag: 'flag_unimputed_jump_curve', param: 'percent_cutoff' },
        'below_threshold_percent_CURVE': { flag: 'flag_below_threshold_curve', param: 'percent_cutoff' },
        'rise_duration_CURVE': { flag: 'flag_incomplete_curve_start_curve', param: 'duration_cutoff' },
        'total_duration_PERIPHERY_BEFORE': { flag: 'flag_incomplete_periphery_before_curve', param: 'duration_cutoff' },
        'rise_imputed_percent_CURVE': { flag: 'flag_imputed_rise_curve', param: 'percent_cutoff_rise' },
        'ascending_imputed_percent_CURVE': { flag: 'flag_imputed_rise_curve', param: 'percent_cutoff_asc' },
        'fall_duration_CURVE': { flag: 'flag_incomplete_curve_end_curve', param: 'duration_cutoff' },
        'total_duration_PERIPHERY_AFTER': { flag: 'flag_incomplete_periphery_after_curve', param: 'duration_cutoff' },
        'fall_imputed_percent_CURVE': { flag: 'flag_imputed_fall_curve', param: 'percent_cutoff_fall' },
        'descending_imputed_percent_CURVE': { flag: 'flag_imputed_fall_curve', param: 'percent_cutoff_desc' },
        'total_gaps_and_non_wear_percent_PERIPHERY_BEFORE': { flag: 'flag_gaps_and_non_wear_periphery_before', param: 'percent_cutoff' },
        'total_extreme_negative_percent_PERIPHERY_BEFORE': { flag: 'flag_extreme_negative_periphery_before', param: 'percent_cutoff' },
        'unimputed_low_quality_percent_PERIPHERY_BEFORE': { flag: 'flag_unimputed_low_quality_periphery_before', param: 'percent_cutoff' },
        'total_gaps_and_non_wear_percent_PERIPHERY_AFTER': { flag: 'flag_gaps_and_non_wear_periphery_after', param: 'percent_cutoff' },
        'total_extreme_negative_percent_PERIPHERY_AFTER': { flag: 'flag_extreme_negative_periphery_after', param: 'percent_cutoff' },
        'unimputed_low_quality_percent_PERIPHERY_AFTER': { flag: 'flag_unimputed_low_quality_periphery_after', param: 'percent_cutoff' }
      }
      
      // Try to get threshold from backend flag_selections first
      let threshold
      if (this.flagSelections) {
        const mapping = flagMapping[key]
        if (mapping && this.flagSelections[mapping.flag] && this.flagSelections[mapping.flag][mapping.param] !== undefined) {
          threshold = this.flagSelections[mapping.flag][mapping.param]
        }
      }
      
      // Fallback to hardcoded defaults (for backward compatibility or if flag_selections not available)
      if (threshold === undefined) {
        const defaultThresholds = {
          'unimputed_low_quality_percent_CURVE': 0.05,
          'unimputed_low_quality_duration_CURVE': 0.5,
          'imputed_percent_CURVE': 0.35,
          'imputed_duration_CURVE': 5.0,
          'unimputed_jump_percent_CURVE': 0.000001,
          'below_threshold_percent_CURVE': 0.5,
          'rise_duration_CURVE': 7 / 60, // duration_cutoff: flag if rise_duration < 7 minutes (in hours)
          'total_duration_PERIPHERY_BEFORE': 0.5, // duration_cutoff: flag if periphery before < 30 minutes (0.5 hours)
          'rise_imputed_percent_CURVE': 0.60,
          'ascending_imputed_percent_CURVE': 0.60,
          'fall_duration_CURVE': 7 / 60, // duration_cutoff: flag if fall_duration < 7 minutes (in hours)
          'total_duration_PERIPHERY_AFTER': 0.5, // duration_cutoff: flag if periphery after < 30 minutes (0.5 hours)
          'fall_imputed_percent_CURVE': 0.70,
          'descending_imputed_percent_CURVE': 0.70,
          'total_gaps_and_non_wear_percent_PERIPHERY_BEFORE': 0.50,
          'total_extreme_negative_percent_PERIPHERY_BEFORE': 0.95,
          'unimputed_low_quality_percent_PERIPHERY_BEFORE': 0.25,
          'total_gaps_and_non_wear_percent_PERIPHERY_AFTER': 0.50,
          'total_extreme_negative_percent_PERIPHERY_AFTER': 0.95,
          'unimputed_low_quality_percent_PERIPHERY_AFTER': 0.25
        }
        threshold = defaultThresholds[key] || 0
      }
      
      // Convert threshold from hours to minutes for display if this is a duration-based flag
      if (key === 'rise_duration_CURVE' || key === 'fall_duration_CURVE') {
        return threshold * 60 // Convert hours to minutes
      }
      
      return threshold
    },
    getFlagType(key) {
      // Determine if flag is triggered by values above or below threshold
      const belowThresholdFlags = [
        'rise_duration_CURVE',
        'total_duration_PERIPHERY_BEFORE',
        'fall_duration_CURVE',
        'total_duration_PERIPHERY_AFTER'
      ]
      return belowThresholdFlags.includes(key) ? 'below' : 'above'
    },
    getFlagStatusText(actualFlagName) {
      // actualFlagName is the FLAG_* name (e.g., "FLAG_unimputed_low_quality_curve")
      // Check if any curves are flagged by this flag
      const flaggedCount = this.curveFeatures.filter(curve => {
        const flagValue = curve[actualFlagName]
        return flagValue != null && Number(flagValue) === 1
      }).length
      
      if (flaggedCount === 0) {
        return 'No Flags'
      } else if (flaggedCount === this.totalCurves) {
        return 'All Flagged'
      } else {
        return `${flaggedCount} Flagged`
      }
    },
    getFlagStatusClass(actualFlagName) {
      // actualFlagName is the FLAG_* name (e.g., "FLAG_unimputed_low_quality_curve")
      // Check if any curves are flagged by this flag
      const flaggedCount = this.curveFeatures.filter(curve => {
        const flagValue = curve[actualFlagName]
        return flagValue != null && Number(flagValue) === 1
      }).length
      
      if (flaggedCount === 0) {
        return 'bg-green-50 text-green-700 border border-green-200'
      } else if (flaggedCount === this.totalCurves) {
        return 'bg-red-50 text-red-700 border border-red-200'
      } else {
        return 'bg-yellow-50 text-yellow-700 border border-yellow-200'
      }
    },
    
    // Open DataExplorer for a specific curve with single tab restriction
    openDataExplorerForCurve(curveIndex) {
      try {
        console.log(`🔗 Opening DataExplorer for curve ${curveIndex}`)
        
        // Validate curve index
        if (curveIndex < 0 || curveIndex >= this.curveFeatures.length) {
          console.warn(`⚠️ Invalid curve index: ${curveIndex}. Valid range: 0-${this.curveFeatures.length - 1}`)
          alert(`Invalid curve index. Please select a curve between 1 and ${this.curveFeatures.length}.`)
          return
        }
        
        // Check if DataExplorer is already open
        const existingExplorer = this.checkForExistingDataExplorer()
        
        if (existingExplorer) {
          console.log('📌 DataExplorer already open, updating existing tab')
          this.updateExistingDataExplorer(existingExplorer, curveIndex)
        } else {
          console.log('🆕 Opening new DataExplorer tab')
          this.openNewDataExplorer(curveIndex)
        }
      } catch (error) {
        console.error('❌ Error opening DataExplorer:', error)
        alert('Error opening Data Explorer. Please try again.')
      }
    },
    
    // Check if DataExplorer is already open
    checkForExistingDataExplorer() {
      try {
        const trackingData = sessionStorage.getItem('dataExplorerOpen')
        if (!trackingData) {
          return null
        }
        
        const data = JSON.parse(trackingData)
        
        // Check if the tracking data is recent (within last 5 minutes)
        const fiveMinutesAgo = Date.now() - (5 * 60 * 1000)
        if (data.timestamp && data.timestamp < fiveMinutesAgo) {
          console.log('🕐 DataExplorer tracking data is stale, treating as closed')
          sessionStorage.removeItem('dataExplorerOpen')
          return null
        }
        
        return data
      } catch (error) {
        console.warn('⚠️ Error checking for existing DataExplorer:', error)
        return null
      }
    },
    
    // Update existing DataExplorer tab
    updateExistingDataExplorer(trackingData, curveIndex) {
      try {
        // Try to find the existing window by name
        const existingWindow = window.open('', 'dataExplorer')
        
        if (existingWindow && !existingWindow.closed) {
          // Window exists and is not closed
          console.log('✅ Found existing DataExplorer window, updating curve')
          
          // Focus the existing window
          existingWindow.focus()
          
          // Send message to update the curve
          existingWindow.postMessage({
            type: 'UPDATE_CURVE',
            curveIndex: curveIndex
          }, window.location.origin)
          
          // Update tracking data
          trackingData.curveIndex = curveIndex
          trackingData.timestamp = Date.now()
          sessionStorage.setItem('dataExplorerOpen', JSON.stringify(trackingData))
          
          console.log(`✅ Updated existing DataExplorer to show curve ${curveIndex}`)
        } else {
          // Window was closed, clean up and open new one
          console.log('🔄 Existing window was closed, opening new one')
          sessionStorage.removeItem('dataExplorerOpen')
          this.openNewDataExplorer(curveIndex)
        }
      } catch (error) {
        console.error('❌ Error updating existing DataExplorer:', error)
        // Fallback: open new tab
        this.openNewDataExplorer(curveIndex)
      }
    },
    
    // Navigate to DataExplorer in same tab
    openNewDataExplorer(curveIndex) {
      try {
        console.log(`✅ Navigating to DataExplorer in same tab for curve ${curveIndex}`)
        this.$router.push(this.toLab(`/explorer?curve=${curveIndex}&from=curve-results`))
      } catch (error) {
        console.error('❌ Error navigating to DataExplorer:', error)
      }
    },
    // Force open in the same tab, bypassing single-tab external window behavior
    openDataExplorerSameTab(curveIndex) {
      try {
        // Clear any stale tracking that might trigger window.open reuse
        sessionStorage.removeItem('dataExplorerOpen')
      } catch (_) {}
      this.openNewDataExplorer(curveIndex)
    },
    loadCurveData() {
      this.loading = true
      this.error = null
      
      try {
        // First try to get curve features from global state (most up-to-date)
        if (this.globalState?.processingResults?.curve_features) {
          console.log('✨ Loading curve features from global state (reactive)')
          const results = this.globalState.processingResults
          this.curveFeatures = results.curve_features
          this.subjectInfo = {
            subid: results.subid,
            dataset_identifier: results.dataset_identifier
          }
          // Load flag selections if available
          if (results.flag_selections) {
            this.flagSelections = results.flag_selections
            console.log('✅ Loaded flag selections from global state')
          }
          console.log('✅ Loaded curve features from global state:', {
            totalCurves: this.curveFeatures.length,
            validCount: this.validCount,
            invalidCount: this.invalidCount,
            subjectInfo: this.subjectInfo
          })
        } 
        // Fallback to sessionStorage if global state isn't populated yet
        else {
          console.log('📦 Falling back to sessionStorage')
          const storedResults = sessionStorage.getItem('processingResults')
          if (storedResults) {
            const results = JSON.parse(storedResults)
            if (results.curve_features && Array.isArray(results.curve_features)) {
              this.curveFeatures = results.curve_features
              this.subjectInfo = {
                subid: results.subid,
                dataset_identifier: results.dataset_identifier
              }
              // Load flag selections if available
              if (results.flag_selections) {
                this.flagSelections = results.flag_selections
                console.log('✅ Loaded flag selections from sessionStorage')
              }
              console.log('Loaded curve features from sessionStorage:', {
                totalCurves: this.curveFeatures.length,
                validCount: this.validCount,
                invalidCount: this.invalidCount,
                subjectInfo: this.subjectInfo
              })
            } else {
              this.error = 'No curve features found in processing results'
            }
          } else {
            this.error = 'No processing results found. Please run data processing first.'
          }
        }
      } catch (error) {
        console.error('Error loading curve data:', error)
        this.error = 'Failed to load curve data: ' + error.message
      } finally {
        this.loading = false
      }
    },

    handleCurveFeaturesUpdated(event) {
      console.log('🔄 CurveResults: Curve features updated event received')
      if (event.detail?.curveFeatures) {
        this.curveFeatures = event.detail.curveFeatures
        console.log('✅ CurveResults: Updated curve features from event:', {
          totalCurves: this.curveFeatures.length,
          validCount: this.validCount,
          invalidCount: this.invalidCount
        })
      }
    },
    async downloadFeatureStatistics() {
      try {
        // Validate data before export
        if (!validateCurveFeaturesData({
          curveMetrics: this.curveMetrics,
          curveStats: this.curveStats,
          subjectInfo: this.subjectInfo
        })) {
          alert('Unable to export statistics: Missing required data')
          return
        }

        // Call the export utility
        await downloadCurveFeaturesStatistics({
          curveMetrics: this.curveMetrics,
          curveStats: this.curveStats,
          subjectInfo: this.subjectInfo,
          totalCurves: this.totalCurves,
          validCount: this.validCount,
          invalidCount: this.invalidCount,
          curveFeatures: this.curveFeatures
        })
      } catch (error) {
        console.error('Failed to download feature statistics:', error)
        alert('Failed to download statistics. Please try again.')
      }
    },
    async downloadFlagStatistics() {
      try {
        // Validate data before export
        if (!validateFlagStatisticsData({
          flagCategories: this.flagCategories,
          flagStats: this.flagStats,
          subjectInfo: this.subjectInfo
        })) {
          alert('Unable to export statistics: Missing required data')
          return
        }

        // Call the export utility
        await downloadFlagStatistics({
          flagCategories: this.flagCategories,
          flagStats: this.flagStats,
          subjectInfo: this.subjectInfo,
          totalCurves: this.totalCurves,
          flaggedCount: this.flaggedCount,
          unflaggedCount: this.unflaggedCount,
          overriddenCurveEntries: this.overriddenCurveEntries,
          curveFeatures: this.curveFeatures
        })
      } catch (error) {
        console.error('Failed to download flag statistics:', error)
        alert('Failed to download statistics. Please try again.')
      }
    }
  },
  mounted() {
    // Set up back button to return to dashboard
    this.setBackButton({
      text: 'Return to Dashboard',
      route: '/maptac/demo/dashboard'
    })
    
    // Listen for curve feature updates from other components
    window.addEventListener('curve-features-updated', this.handleCurveFeaturesUpdated)
    console.log('✅ CurveResults: Listening for curve feature updates')
    
    this.loadCurveData()
  },

  beforeUnmount() {
    // Clean up event listener
    window.removeEventListener('curve-features-updated', this.handleCurveFeaturesUpdated)
    console.log('🧹 CurveResults: Removed curve feature update listener')
  }
}
</script>
